import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useGraphQLAuth } from 'hooks/useGraphQLAuth';
import { useToast } from 'hooks/useToast';
import { Button } from 'components/Button';
import { iconArrowRight } from 'assets/icons';

export interface EventFaq {
  eventFaqId: string;
  eventId: string;
  question: string;
  answer: string;
  displayOrder: number;
  isActive: boolean;
  insertDate: string;
  updateDate: string;
}

interface FaqRowProps {
  question: string;
  answer: string;
  displayOrder: number;
  isActive: string;
  actions: any;
}

interface Property {
  name: string;
  value: string;
}

export const useFaqsTable = (onEditFaq?: (faq: EventFaq) => void) => {
  const { id: eventId } = useParams<{ id: string }>();
  const { connectGraphQLApi } = useGraphQLAuth();
  const { setToast } = useToast();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [properties, setProperties] = useState<Property[]>([]);
  const [faqRows, setFaqRows] = useState<FaqRowProps[]>([]);
  const [faqs, setFaqs] = useState<EventFaq[]>([]);
  
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cancel previous request
  const cancelRequest = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
  };

  useEffect(() => {
    getFaqs();
    
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [eventId]);

  const getActionButtons = (faq: EventFaq) => {
    return [
      <Button
        key="edit"
        label=""
        size="small"
        icon={iconArrowRight}
        onlyIcon={true}
        onClick={() => onEditFaq && onEditFaq(faq)}
        title="Editar FAQ"
      />
    ];
  };

  /* API CALLS */
  const getFaqs = async () => {
    setLoading(true);
    cancelRequest();

    const query = `
      query BackofficeFaqs($eventId: ID!) {
        backofficeFaqs(eventId: $eventId) {
          eventFaqId
          eventId
          question
          answer
          displayOrder
          isActive
          insertDate
          updateDate
        }
      }
    `;

    try {
      const response = await connectGraphQLApi({
        query,
        variables: { 
          eventId,
        },
        errorMessage: 'Falha ao listar FAQs',
        useNewApi: true,
      });

      setError(false);
      const responseData = (response as any).data;
      const eventFaqs: EventFaq[] = responseData.backofficeFaqs || [];
      
      // Sort FAQs by displayOrder (already sorted by backend, but keeping for safety)
      // const sortedFaqs = eventFaqs.sort((a, b) => a.displayOrder - b.displayOrder);
      
      // Get count and calculate properties
      await getFaqsCount(eventFaqs);
      
      const faqRows = eventFaqs.map((faq: EventFaq) => {
        const actions = getActionButtons(faq);
        
        return ({
          question: faq.question,
          answer: faq.answer.length > 100 ? `${faq.answer.substring(0, 100)}...` : faq.answer,
          displayOrder: faq.displayOrder,
          isActive: faq.isActive ? 'Ativo' : 'Inativo',
          actions,
        });
      });

      setFaqs(eventFaqs);
      setFaqRows(faqRows);  
      setLoading(false);
    } catch (error: any) {
      console.error('Error details:', error);
      setError(true);
      setLoading(false);
      setToast({
        type: 'error',
        message: 'Falha ao listar FAQs',
        isActive: true,
        timeToRemoveToast: 3000,
      });
    }
  };

  const getFaqsCount = async (faqs?: EventFaq[]) => {
    try {
      const query = `
        query BackofficeFaqsCount($eventId: ID!) {
          backofficeFaqsCount(eventId: $eventId)
        }
      `;

      const response = await connectGraphQLApi({
        query,
        variables: { eventId },
        errorMessage: 'Falha ao contar FAQs',
        useNewApi: true,
      });

      const responseData = (response as any).data;
      const totalCount = responseData.backofficeFaqsCount || 0;
      
      // If we have faqs array from the previous call, use it for active/inactive count
      // Otherwise, use the total count for now
      let activeCount = 0;
      let inactiveCount = 0;
      
      if (faqs) {
        activeCount = faqs.filter(f => f.isActive).length;
        console.log('faqs!!', faqs);
        inactiveCount = faqs.filter(f => !f.isActive).length;
      } else {
        // If no faqs array provided, we can't calculate active/inactive accurately
        activeCount = totalCount;
        inactiveCount = 0;
      }
      
      // Create properties based on the FAQs count
      const properties: Property[] = [
        { name: 'Total de FAQs', value: totalCount.toString() },
        { name: 'FAQs Ativas', value: activeCount.toString() },
        { name: 'FAQs Inativas', value: inactiveCount.toString() },
      ];
      
      setProperties(properties);
    } catch (error: any) {
      console.error('Error getting FAQ count:', error);
      // Don't show error toast for count, just log it
    }
  };

  return {
    loading,
    error,
    properties,
    faqRows,
    faqs,
    eventId
  };
}; 